'use client';

import classnames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { capitalizeStringFirstLetter } from '@cineamo/ui/src/helpers/string-helper';

import { IconForth16 } from '@cineamo/ui/src/icons/ic-forth';

type BreadcrumbItem = {
    breadcrumb: string;
    href: string;
};

const convertBreadcrumb = (string: string) => {
    const stringToReturn = string
        .replace(/-/g, ' ')
        .replace(/oe/g, 'ö')
        .replace(/ae/g, 'ä')
        .replace(/ue/g, 'ü')
        .replace(/cineamo/g, 'Cineamo')
        .replace(/faq/g, 'FAQs');
    return capitalizeStringFirstLetter(stringToReturn);
};

export default function BreadcrumbNavigation() {
    const pathname = usePathname();

    const t = useTranslations();

    const breadcrumbs = useMemo<BreadcrumbItem[] | null>(() => {
        if (!pathname) return null;

        const linkPath = pathname.split('/');
        linkPath.shift();

        // Remove language part like /de or /en
        if (linkPath.length > 0 && ['de', 'en'].includes(linkPath[0].toLowerCase())) {
            linkPath.shift();
        }

        return linkPath.map((path, i) => {
            const pathWithoutQuery = path.split('?')[0];
            return {
                breadcrumb: pathWithoutQuery,
                href: '/' + linkPath.slice(0, i + 1).join('/')
            };
        });
    }, [pathname]);

    if (!breadcrumbs) {
        return null;
    }

    return (
        <nav aria-label="">
            <ul className="flex tracking-regular text-white items-center space-x-6">
                <li>
                    {pathname !== '/' ? (
                        <Link href="/">
                            <div
                                className={classnames(
                                    'text-white hover:underline hover:text-white/100 hover:text',
                                    breadcrumbs?.length > 0 ? 'text-white/50' : null
                                )}>
                                {t('common.navigation.homepage')}
                            </div>
                        </Link>
                    ) : null}
                </li>
                {breadcrumbs.map((breadcrumb, i) => {
                    const link = i !== breadcrumbs.length - 1 ? breadcrumb.href : null;
                    const part = (
                        <div
                            className={`${i !== breadcrumbs.length - 1 && 'hover:underline cursor-pointer'} ${
                                i === breadcrumbs.length ? 'text-white/50' : 'text-white'
                            }`}>
                            {convertBreadcrumb(breadcrumb.breadcrumb)}
                        </div>
                    );
                    return (
                        <li
                            key={i}
                            className="flex flex-row items-center space-x-6">
                            {breadcrumbs?.length > 0 ? <IconForth16 className={'text-white/50'} /> : null}
                            {link ? <Link href={link}>{part}</Link> : part}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
